import React from 'react';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography, Card, CardContent, useMediaQuery, Box, Slide } from '@mui/material';
import { useTheme } from '@mui/material/styles';

const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="up" in timeout={1000} ref={ref} {...props} />;
});

const QuizMessage = ({ showOptions, open, handleRetake, handleContinue, handleExit, certificateData, lngsltd }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm')); // Check if screen size is small

    return (
        <>
            {/* Congratulations Options Dialog */}
            <Dialog
                open={showOptions}
                TransitionComponent={Transition}
                onClose={(event, reason) => {
                    // Prevent closing if the click is outside or by pressing Escape
                    if (reason !== 'backdropClick' && reason !== 'escapeKeyDown') {
                        handleRetake();
                    }
                }}
                hideBackdrop
                maxWidth="sm"
                fullWidth
                disableEscapeKeyDown
                PaperProps={{
                    sx: {
                        borderRadius: '16px',
                        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
                        margin: isMobile ? '16px' : '24px',
                        padding: 0
                    },
                }}
            >
                <DialogTitle 
                    align="center"
                    sx={{
                        fontSize: '1.75rem',
                        fontWeight: 700,
                        color: '#10b981',
                        pt: 3,
                        pb: 2
                    }}
                >
                    {lngsltd['Congratulations!']}
                </DialogTitle>
                <DialogContent sx={{ px: 3, pb: 2 }}>
                    <Typography 
                        variant="body1" 
                        align="center" 
                        gutterBottom
                        sx={{
                            fontSize: '1.1rem',
                            color: '#6c757d',
                            mb: 3,
                            lineHeight: 1.6
                        }}
                    >
                        {lngsltd['You have successfully completed the course.']}
                    </Typography>
                    <Card 
                        elevation={0}
                        sx={{ 
                            mt: 2,
                            backgroundColor: 'rgba(16, 185, 129, 0.1)',
                            borderRadius: '12px',
                            border: '2px solid rgba(16, 185, 129, 0.2)'
                        }}
                    >
                        <CardContent sx={{ py: 2.5 }}>
                            <Typography 
                                variant="h4" 
                                align="center"
                                sx={{
                                    fontWeight: 700,
                                    color: '#10b981',
                                    fontSize: '2rem'
                                }}
                            >
                                {certificateData.percentage}%
                            </Typography>
                            <Typography 
                                variant="body2" 
                                align="center"
                                sx={{
                                    color: '#6c757d',
                                    mt: 1,
                                    fontSize: '0.9rem'
                                }}
                            >
                                {lngsltd['You have scored']}
                            </Typography>
                        </CardContent>
                    </Card>
                </DialogContent>
                <DialogActions 
                    sx={{ 
                        justifyContent: 'center', 
                        gap: 2,
                        px: 3,
                        pb: 3,
                        pt: 1
                    }}
                >
                    <Button 
                        onClick={handleRetake} 
                        variant="outlined"
                        sx={{
                            borderColor: '#6c757d',
                            color: '#6c757d',
                            fontWeight: 500,
                            px: 4,
                            py: 1.25,
                            borderRadius: '8px',
                            textTransform: 'none',
                            minWidth: '120px',
                            '&:hover': {
                                borderColor: '#5a6268',
                                backgroundColor: 'rgba(108, 117, 125, 0.05)',
                                transform: 'translateY(-1px)',
                                boxShadow: '0 4px 8px rgba(108, 117, 125, 0.2)'
                            },
                            transition: 'all 0.2s ease-in-out'
                        }}
                    >
                        {lngsltd['Retake']}
                    </Button>
                    <Button 
                        onClick={handleContinue} 
                        variant="contained"
                        sx={{
                            backgroundColor: '#10b981',
                            color: '#ffffff',
                            fontWeight: 600,
                            px: 4,
                            py: 1.25,
                            borderRadius: '8px',
                            textTransform: 'none',
                            minWidth: '120px',
                            '&:hover': {
                                backgroundColor: '#059669',
                                transform: 'translateY(-1px)',
                                boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)'
                            },
                            transition: 'all 0.2s ease-in-out'
                        }}
                    >
                        {lngsltd['Continue']}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Non-eligible Case Dialog */}
            <Dialog
                open={open}
                onClose={handleExit}
                maxWidth="sm"
                fullWidth
                disableEscapeKeyDown
                PaperProps={{
                    sx: {
                        borderRadius: '16px',
                        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
                        margin: isMobile ? '16px' : '24px',
                        padding: 0
                    },
                }}
            >
                <DialogTitle 
                    align="center"
                    sx={{
                        fontSize: '1.5rem',
                        fontWeight: 700,
                        color: '#dc3545',
                        pt: 3,
                        pb: 2
                    }}
                >
                    {lngsltd['Try Again!']}
                </DialogTitle>
                <DialogContent sx={{ px: 3, pb: 2 }}>
                    <Typography 
                        variant="body1" 
                        align="center" 
                        sx={{
                            fontSize: '1.1rem',
                            color: '#6c757d',
                            lineHeight: 1.6
                        }}
                    >
                        {lngsltd['Your score is less than 90%.']}
                    </Typography>
                </DialogContent>
                <DialogActions 
                    sx={{ 
                        justifyContent: 'center', 
                        gap: 2,
                        px: 3,
                        pb: 3,
                        pt: 1
                    }}
                >
                    <Button 
                        onClick={handleRetake} 
                        variant="contained"
                        sx={{
                            backgroundColor: '#dc3545',
                            color: '#ffffff',
                            fontWeight: 600,
                            px: 4,
                            py: 1.25,
                            borderRadius: '8px',
                            textTransform: 'none',
                            minWidth: '120px',
                            '&:hover': {
                                backgroundColor: '#b02a37',
                                transform: 'translateY(-1px)',
                                boxShadow: '0 4px 12px rgba(220, 53, 69, 0.3)'
                            },
                            transition: 'all 0.2s ease-in-out'
                        }}
                    >
                        {lngsltd['Retake']}
                    </Button>
                    <Button 
                        onClick={handleExit} 
                        variant="outlined"
                        sx={{
                            borderColor: '#6c757d',
                            color: '#6c757d',
                            fontWeight: 500,
                            px: 4,
                            py: 1.25,
                            borderRadius: '8px',
                            textTransform: 'none',
                            minWidth: '120px',
                            '&:hover': {
                                borderColor: '#5a6268',
                                backgroundColor: 'rgba(108, 117, 125, 0.05)',
                                transform: 'translateY(-1px)',
                                boxShadow: '0 4px 8px rgba(108, 117, 125, 0.2)'
                            },
                            transition: 'all 0.2s ease-in-out'
                        }}
                    >
                        {lngsltd['Exit']}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default QuizMessage;
